import { UnitSystem } from "@/types/geo"
import { MeasurementSystem } from "@/types/user"
import { createSelector } from "@reduxjs/toolkit"

import { composeLocale } from "@/lib/locale"
import { regionAreaUnitSystem, regionDefaults } from "@/lib/region-utils"
import { selectOrgDefaultRegion } from "@/lib/storage/app-slice"
import { RootState } from "@/lib/storage/redux-store"
import { selectUnitSystemPreference } from "@/lib/storage/ui-settings-slice"
import {
  selectUserCurrencyCode,
  selectUserLanguage,
  selectUserMeasurementSystem,
  selectUserRegion,
} from "@/lib/storage/user-slice"

// Per-source readers over localeSlice. These are the explicit inputs to the one
// memoized composition below; consumers should reach for selectLocale rather than
// recomposing from these themselves. The user's saved preference is not mirrored
// here — it lives on the session user (selectUserLanguage/selectUserRegion).
export const selectEmbeddedLocale = (s: RootState) => s.localeSlice.embedded
export const selectBrowserLocale = (s: RootState) => s.localeSlice.browser

// The single, memoized format-locale composition ("language-REGION"). Runs
// composeLocale once per input change, shared by useLocale() (locale-hooks.ts) and
// by imperative callers (thunks/slices) that can't use the hook — so every formatter
// reads one cached value instead of each recomputing. Adding a future source means
// adding an input here and nothing else.
export const selectLocale = createSelector(
  [
    selectUserLanguage,
    selectUserRegion,
    selectEmbeddedLocale,
    selectBrowserLocale,
  ],
  (userLanguage, userRegion, embedded, browser) =>
    composeLocale({
      userLanguage,
      userRegion,
      embeddedLanguage: embedded?.language,
      embeddedRegion: embedded?.region,
      browserLanguage: browser.language,
      browserRegion: browser.region,
    })
)

// Just the language subtag of the resolved locale (e.g. "fr" from "fr-FR"). Use this
// rather than splitting the locale string by hand.
export const selectLanguage = createSelector(
  [selectLocale],
  (locale) => new Intl.Locale(locale).language
)

// Just the region subtag of the resolved locale (e.g. "FR" from "fr-FR"). composeLocale
// always emits a region (defaults to "US"), so this is never undefined.
export const selectRegion = createSelector(
  [selectLocale],
  (locale) => new Intl.Locale(locale).region ?? "US"
)

// The effective measurement system: the user's explicit choice if they made one, otherwise the
// default for their resolved region.
export const selectMeasurementSystem = createSelector(
  [selectUnitSystemPreference, selectUserMeasurementSystem, selectRegion],
  (localPreference, profilePreference, region): MeasurementSystem =>
    localPreference ??
    profilePreference ??
    regionDefaults(region).measurementSystem
)

// Road distances use miles in both US and UK systems.
export const selectUnitSystem = createSelector(
  [selectMeasurementSystem],
  (measurementSystem): UnitSystem =>
    measurementSystem === "metric" ? "metric" : "imperial"
)

// Temperatures use Fahrenheit only for the US system; UK stays Celsius.
export const selectTemperatureUnitSystem = createSelector(
  [selectMeasurementSystem],
  (measurementSystem): UnitSystem =>
    measurementSystem === "us" ? "imperial" : "metric"
)

// The unit system to use for floor/room AREA (m² vs sq ft). Keyed purely on the
// resolved region because area is a real-estate convention that doesn't follow
// the US-vs-world temp/distance split — e.g. Singapore/UK/Canada are "metric"
// but quote rooms in sq ft. Intentionally ignores the manual unit preference,
// which is surfaced to users as a temperature toggle.
export const selectAreaUnitSystem = createSelector(
  [selectRegion],
  (region): UnitSystem => regionAreaUnitSystem(region)
)

// The effective currency code is: user explicit choice, else their resolved region,
// with the org's home country filling in only when the visitor gave no region signal
// at all; mirrors CurrencyService.preferred_currency_code.
export const selectCurrencyCode = createSelector(
  [
    selectUserCurrencyCode,
    selectUserRegion,
    selectEmbeddedLocale,
    selectBrowserLocale,
    selectOrgDefaultRegion,
    selectRegion,
  ],
  (currencyCode, userRegion, embedded, browser, orgRegion, region): string => {
    if (currencyCode) return currencyCode
    const visitorRegion = embedded?.region ?? userRegion ?? browser.region
    return regionDefaults(visitorRegion ? region : (orgRegion ?? region))
      .currency
  }
)
